import * as THREE from 'three';
import { Annotation } from '../Annotation';
import { CameraMode } from '../defines';
import { EventDispatcher } from '../EventDispatcher';
import { Utils } from '../utils';
import { Measure } from '../utils/Measure';
import { View } from './View';
import { BoxVolume } from '../utils/Volume';
import { FirstPersonControls } from '../navigation/FirstPersonControls';
import { InputHandler } from '../navigation/InputHandler';
import { OrbitControls } from '../navigation/OrbitControls';
import { DeviceOrientationControls } from '../navigation/DeviceOrientationControls';
import { EarthControls } from '../navigation/EarthControls';
import { PointCloudOctree } from '../PointCloudOctree';
import { PointCluster } from '../utils/PointCluster';
import { Orthophoto } from '../utils/Orthophoto';
import { PointoramaPlane } from '../utils/PointoramaPlane';
import { PointoramaOutline } from '../utils/PointoramaOutline';
import { PointoramaOpeningSketch } from '../utils/PointoramaOpeningSketch';
import { PointoramaRoom } from '../utils/PointoramaRoom';
import { PointoramaWallSketch } from '../utils/PointoramaWallSketch';
import { PointoramaFloorMesh3D } from '../utils/PointoramaFloorMesh3D';
import { PointoramaSketchObject } from '../utils/PointoramaSketchObject';
import { PointoramaStructure } from '../utils/PointoramaStructure';
import { PointoramaSurface } from '../utils/PointoramaSurface';
import { Viewer } from './viewer';

import { DefaultColors } from '../defines';
import { clearTileTracking } from '../Potree';

export class Scene extends EventDispatcher {
  annotations: Annotation;
  scene: THREE.Scene;
  sceneBG: THREE.Scene;
  scenePointCloud: THREE.Scene;
  sceneDebug: THREE.Scene; // Debug scene that renders on top of everything
  cameraP: THREE.PerspectiveCamera;
  cameraO: THREE.OrthographicCamera;
  cameraVR: THREE.PerspectiveCamera;
  cameraBG: THREE.Camera;
  cameraScreenSpace: THREE.OrthographicCamera;
  cameraMode: number;
  overrideCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera | null;
  pointclouds: PointCloudOctree[];
  measurements: Measure[];
  volumes: BoxVolume[];
  orthophotos: Orthophoto[];
  planes: PointoramaPlane[];
  rooms: PointoramaRoom[];
  wallSketches: PointoramaWallSketch[];
  floorMeshes3D: PointoramaFloorMesh3D[];
  /** Objects drawn only in 3D (perspective) views — hidden for each 2D view's render, like the floor meshes (e.g. custom objects' 3D models). */
  objects3DOnly: Set<THREE.Object3D>;
  outlines: PointoramaOutline[];
  openingSketches: PointoramaOpeningSketch[];
  sketchObjects: PointoramaSketchObject[];
  surfaces: PointoramaSurface[];
  structures: PointoramaStructure[];
  polygonClipVolumes: THREE.Object3D[];
  cameraAnimations: THREE.Object3D[];
  geopackages: Array<{ node: THREE.Object3D }>;
  pointClusters: PointCluster[];
  fpControls: FirstPersonControls | null;
  orbitControls: OrbitControls | null;
  earthControls: EarthControls | null;
  deviceControls: DeviceOrientationControls | null;
  inputHandler: InputHandler | null;
  view: View;
  directionalLight: THREE.DirectionalLight | null;
  referenceFrame?: THREE.Object3D;
  lengthUnit?: { unitspermeter: number } | null;
  lengthUnitDisplay?: { unitspermeter: number; code: string } | null;
  fov = 60;
  /**
   * Vertex-sphere scale per annotation family: full size while the
   * corresponding draw/edit tool is active, shrunken otherwise. Lives on the
   * scene (not the tools) because the entities rebuild their spheres on every
   * redux sync — which can happen while no tool is active — and must re-apply
   * the current size. `measurementSphereScale` also covers surfaces and
   * door/window openings (their annotations live in `measurements`).
   */
  measurementSphereScale = 0.5;
  roomSphereScale = 0.5;
  structureSphereScale = 0.5;
  outlineSphereScale = 0.5;
  /** Wall-sketch node markers are oversized (1.2) in the layout editor — easier to grab. */
  wallSketchSphereScale = 0.6;
  viewer: Viewer;
  // Ordered identifiers for render sorting of annotations and other elements
  orderedIdentifiers: Array<{ identifier: string; index: number }>;

  // Expose DefaultColors to allow access in other modules
  readonly DefaultColors = DefaultColors;

  constructor(viewer: Viewer) {
    super();
    this.viewer = viewer;

    this.annotations = new Annotation();

    this.scene = new THREE.Scene();
    this.sceneBG = new THREE.Scene();
    this.scenePointCloud = new THREE.Scene();
    this.sceneDebug = new THREE.Scene(); // Debug scene for rendering on top

    this.cameraP = new THREE.PerspectiveCamera(this.fov, 1, 0.1, 1000 * 1000);
    this.cameraO = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000 * 1000);
    this.cameraVR = new THREE.PerspectiveCamera();
    this.cameraBG = new THREE.Camera();
    this.cameraScreenSpace = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
    this.cameraMode = CameraMode.PERSPECTIVE;
    this.overrideCamera = null;
    this.pointclouds = [];

    this.measurements = [];
    this.volumes = [];
    this.planes = [];
    this.rooms = [];
    this.wallSketches = [];
    this.floorMeshes3D = [];
    this.objects3DOnly = new Set();
    this.outlines = [];
    this.openingSketches = [];
    this.sketchObjects = [];
    this.surfaces = [];
    this.structures = [];
    this.polygonClipVolumes = [];
    this.cameraAnimations = [];
    this.geopackages = [];
    this.pointClusters = [];
    this.orthophotos = [];

    this.fpControls = null;
    this.orbitControls = null;
    this.earthControls = null;
    this.deviceControls = null;
    this.inputHandler = null;

    this.view = new View();

    this.directionalLight = null;

    // Initialize ordered identifiers
    this.orderedIdentifiers = [];

    this.initialize();
  }

  getBoundingBox(pointclouds = this.pointclouds) {
    const box = new THREE.Box3();

    this.scenePointCloud.updateMatrixWorld(true);
    this.referenceFrame?.updateMatrixWorld(true);

    for (const pointcloud of pointclouds) {
      pointcloud.updateMatrixWorld(true);

      const pointcloudBox = pointcloud.pcoGeometry.tightBoundingBox ? pointcloud.pcoGeometry.tightBoundingBox : pointcloud.boundingBox;
      const boxWorld = Utils.computeTransformedBoundingBox(pointcloudBox, pointcloud.matrixWorld);
      box.union(boxWorld);
    }

    return box;
  }

  addPointCloud(pointcloud: PointCloudOctree) {
    // Set the viewer backref BEFORE binding reactive properties so the hidden-classifications
    // and hidden-sources subscriptions can access viewer.classificationsInfo / viewer.sourcesInfo.
    pointcloud.viewer = this.viewer as unknown as typeof window.viewer;
    pointcloud.bindReactiveProperties();

    this.pointclouds.push(pointcloud);
    this.scenePointCloud.add(pointcloud);

    this.dispatchEvent({
      type: 'pointcloud_added',
      pointcloud: pointcloud,
    });
  }

  removePointCloud(identifier: string) {
    const pointCloud = this.pointclouds.find((e) => e.identifier === identifier);
    if (!pointCloud) return;

    this.pointclouds = this.pointclouds.filter((e) => e.identifier !== identifier);
    this.scenePointCloud.remove(pointCloud);

    // Tear down reactive property subscriptions before disposing material
    pointCloud.disposeReactiveProperties();

    // Dispose geometry caches (PPCPointCloudGeometry)
    if (pointCloud.pcoGeometry?.dispose) {
      pointCloud.pcoGeometry.dispose();
    }

    // Dispose material
    if (pointCloud.material?.dispose) {
      pointCloud.material.dispose();
    }

    // Dispose LRU cache
    if (pointCloud.lru) {
      pointCloud.lru.dispose();
    }

    // Clear global tile tracking for this point cloud
    clearTileTracking();

    this.dispatchEvent({
      type: 'pointcloud_removed',
      pointcloud: pointCloud,
    });
  }

  addVolume(volume: BoxVolume) {
    this.volumes.push(volume);
    this.dispatchEvent({
      type: 'volume_added',
      scene: this,
      volume: volume,
    });
  }

  addPointCluster(pointCluster: PointCluster) {
    this.pointClusters.push(pointCluster);
  }

  addPlane(plane: PointoramaPlane) {
    this.planes.push(plane);
    this.viewer.planeScene.add(plane);
    this.viewer.invalidatePlaneScale();
  }

  addRoom(room: PointoramaRoom) {
    this.rooms.push(room);
    this.viewer.floorplanTool.scene.add(room);

    this.dispatchEvent({
      type: 'room_added',
      room: room,
    });
  }

  addWallSketch(skeleton: PointoramaWallSketch) {
    this.wallSketches.push(skeleton);
    this.viewer.floorplanTool.scene.add(skeleton);

    this.dispatchEvent({
      type: 'wall_sketch_added',
      skeleton: skeleton,
    });
  }

  addOutline(outline: PointoramaOutline) {
    this.outlines.push(outline);
    this.viewer.floorplanTool.scene.add(outline);

    this.dispatchEvent({
      type: 'outline_added',
      outline: outline,
    });
  }

  addOpeningSketch(openingSketch: PointoramaOpeningSketch) {
    this.openingSketches.push(openingSketch);
    this.viewer.floorplanTool.scene.add(openingSketch);

    this.dispatchEvent({
      type: 'opening_sketch_added',
      openingSketch,
    });
  }

  removeOpeningSketch(openingSketch: PointoramaOpeningSketch) {
    const index = this.openingSketches.findIndex((o) => o.identifier === openingSketch.identifier);
    if (index > -1) {
      const groupIdentifier = openingSketch.groupIdentifier;
      this.openingSketches.splice(index, 1);
      openingSketch.dispose();
      this.viewer.floorplanTool.scene.remove(openingSketch);

      this.dispatchEvent({
        type: 'opening_sketch_removed',
        openingSketch,
        groupIdentifier,
      });
    }
  }

  addSketchObject(sketchObject: PointoramaSketchObject) {
    this.sketchObjects.push(sketchObject);
    this.viewer.floorplanTool.scene.add(sketchObject);

    this.dispatchEvent({
      type: 'sketch_object_added',
      sketchObject,
    });
  }

  removeSketchObject(sketchObject: PointoramaSketchObject) {
    const index = this.sketchObjects.findIndex((o) => o.identifier === sketchObject.identifier);
    if (index > -1) {
      const groupIdentifier = sketchObject.groupIdentifier;
      this.sketchObjects.splice(index, 1);
      sketchObject.dispose();
      this.viewer.floorplanTool.scene.remove(sketchObject);

      this.dispatchEvent({
        type: 'sketch_object_removed',
        sketchObject,
        groupIdentifier,
      });
    }
  }

  addFloorMesh3D(mesh: PointoramaFloorMesh3D) {
    this.floorMeshes3D.push(mesh);
    this.viewer.floorplanTool.scene.add(mesh);
  }

  addSurface(surface: PointoramaSurface) {
    this.surfaces.push(surface);
    this.viewer.planeScene.add(surface);
    this.viewer.invalidatePlaneScale();
  }

  addStructure(structure: PointoramaStructure) {
    this.structures.push(structure);
    this.viewer.floorplanTool.scene.add(structure);
  }

  addGeopackage(geopackage: { node: THREE.Object3D }) {
    this.geopackages.push(geopackage);
    this.scene.add(geopackage.node);

    this.dispatchEvent({
      type: 'geopackage_added',
      scene: this,
      geopackage: geopackage,
    });
  }

  removeGeopackage(geopackage: { node: THREE.Object3D }) {
    const index = this.geopackages.indexOf(geopackage);
    if (index > -1) {
      this.geopackages.splice(index, 1);

      this.dispatchEvent({
        type: 'geopackage_removed',
        scene: this,
        geopackage: geopackage,
      });
    }
  }

  addOrthophotoLayer(identifier: string, elevation: number) {
    const existingOrthophoto = this.orthophotos.find((orthophoto) => orthophoto.identifier === identifier);
    if (existingOrthophoto) {
      return existingOrthophoto;
    } else {
      const newOrthophoto = new Orthophoto(identifier, elevation, this);
      this.orthophotos.push(newOrthophoto);
      this.scene.add(newOrthophoto);
      return newOrthophoto;
    }
  }

  removeOrthophotoLayer({ identifier }: { identifier: string }) {
    const orthophotoToRemove = this.orthophotos.find((photo) => photo.identifier === identifier);
    if (!orthophotoToRemove) return;
    this.scene.remove(orthophotoToRemove);
    this.orthophotos = this.orthophotos.filter((photo) => photo.identifier !== identifier);
  }

  removeVolume(volume: BoxVolume) {
    const index = this.volumes.indexOf(volume);
    if (index > -1) {
      this.volumes.splice(index, 1);

      this.dispatchEvent({
        type: 'volume_removed',
        scene: this,
        volume: volume,
      });
    }
  }

  removePointCluster(pointCluster: PointCluster) {
    const index = this.pointClusters.indexOf(pointCluster);
    if (index > -1) {
      this.pointClusters.splice(index, 1);
    }
  }

  removePlane(plane: PointoramaPlane) {
    const index = this.planes.findIndex((p) => p.identifier === plane.identifier);
    if (index > -1) {
      this.planes.splice(index, 1);
    }

    this.viewer.planeScene.remove(plane);
  }

  removeRoom(room: PointoramaRoom) {
    const index = this.rooms.findIndex((r) => r.identifier === room.identifier);
    if (index > -1) {
      const groupIdentifier = room.groupIdentifier;
      this.rooms.splice(index, 1);
      room.dispose();
      this.viewer.floorplanTool.scene.remove(room);

      this.dispatchEvent({
        type: 'room_removed',
        room: room,
        groupIdentifier: groupIdentifier,
      });
    }
  }

  removeWallSketch(skeleton: PointoramaWallSketch) {
    const index = this.wallSketches.findIndex((s) => s.identifier === skeleton.identifier);
    if (index > -1) {
      const groupIdentifier = skeleton.groupIdentifier;
      this.wallSketches.splice(index, 1);
      skeleton.dispose();
      this.viewer.floorplanTool.scene.remove(skeleton);

      this.dispatchEvent({
        type: 'wall_sketch_removed',
        skeleton: skeleton,
        groupIdentifier: groupIdentifier,
      });
    }
  }

  removeOutline(outline: PointoramaOutline) {
    const index = this.outlines.findIndex((o) => o.identifier === outline.identifier);
    if (index > -1) {
      const groupIdentifier = outline.groupIdentifier;
      this.outlines.splice(index, 1);
      outline.dispose();
      this.viewer.floorplanTool.scene.remove(outline);

      this.dispatchEvent({
        type: 'outline_removed',
        outline: outline,
        groupIdentifier: groupIdentifier,
      });
    }
  }

  removeFloorMesh3D(mesh: PointoramaFloorMesh3D) {
    const index = this.floorMeshes3D.findIndex((m) => m.identifier === mesh.identifier);
    if (index > -1) {
      this.floorMeshes3D.splice(index, 1);
      mesh.dispose();
      this.viewer.floorplanTool.scene.remove(mesh);
    }
  }

  removeSurface(surface: PointoramaSurface) {
    const index = this.surfaces.findIndex((s) => s.identifier === surface.identifier);
    if (index > -1) {
      this.surfaces.splice(index, 1);
      surface.dispose();
      this.viewer.planeScene.remove(surface);
    }
  }

  removeStructure(structure: PointoramaStructure) {
    const index = this.structures.findIndex((s) => s.identifier === structure.identifier);
    if (index > -1) {
      this.structures.splice(index, 1);
      structure.dispose();
      this.viewer.floorplanTool.scene.remove(structure);
    }
  }

  addCameraAnimation(animation: THREE.Object3D) {
    this.cameraAnimations.push(animation);
    this.dispatchEvent({
      type: 'camera_animation_added',
      scene: this,
      animation: animation,
    });
  }

  removeCameraAnimation(animation: THREE.Object3D) {
    const index = this.cameraAnimations.indexOf(animation);
    if (index > -1) {
      this.cameraAnimations.splice(index, 1);

      this.dispatchEvent({
        type: 'camera_animation_removed',
        scene: this,
        animation: animation,
      });
    }
  }

  addPolygonClipVolume(volume: THREE.Object3D) {
    this.polygonClipVolumes.push(volume);
    this.dispatchEvent({
      type: 'polygon_clip_volume_added',
      scene: this,
      volume: volume,
    });
  }

  removePolygonClipVolume(volume: THREE.Object3D) {
    const index = this.polygonClipVolumes.indexOf(volume);
    if (index > -1) {
      this.polygonClipVolumes.splice(index, 1);
      this.dispatchEvent({
        type: 'polygon_clip_volume_removed',
        scene: this,
        volume: volume,
      });
    }
  }

  addMeasurement(measurement: Measure) {
    measurement.lengthUnit = this.lengthUnit;
    measurement.lengthUnitDisplay = this.lengthUnitDisplay;
    this.measurements.push(measurement);
    this.viewer.measurementScene.add(measurement);
  }

  finishMeasurement(measure: Measure, opts: { groupIdentifier?: string | null; skipSelection?: boolean } = {}) {
    measure.finishMeasurement();
    // Mirror the group onto the scene object so floor isolation can read it
    // immediately, without waiting for the GraphQL sync round-trip.
    if (opts.groupIdentifier !== undefined) measure.groupIdentifier = opts.groupIdentifier;
    // `groupIdentifier`, when supplied, pins the new annotation to a specific
    // floor / annotation group. The React event handler (usePotreeEvents)
    // normally derives the group from the user's currently-selected sidebar
    // entry; tools that programmatically create annotations bound to a known
    // group (door/window detection on a floor's skeleton, etc.) need to
    // override that default so the annotation lands in the right place.
    //
    // `skipSelection` suppresses the auto-select-the-new-annotation dispatch
    // in usePotreeEvents — needed by tools that finish a measurement mid-flow
    // (door/window LineTool committing a draw on a focused wall) where the
    // sidebar-driven view jump would yank the user out of their context.
    this.dispatchEvent({ type: 'measurement_finish', value: { measure, groupIdentifier: opts.groupIdentifier, skipSelection: opts.skipSelection } });
  }

  removeMeasurement(measurement: Measure) {
    const index = this.measurements.indexOf(measurement);
    if (index > -1) {
      this.measurements.splice(index, 1);
      this.viewer.measurementScene.remove(measurement);
      // Release reactive-property subscriptions so the annotation can be
      // garbage-collected. Without this, every recreate-on-undo leaks the
      // old instance + 3–4 live subscriptions, and repeated undo/redo
      // across an area deletion progressively tanks performance.
      measurement.dispose();
    }
  }

  removeAllMeasurements() {
    while (this.measurements.length > 0) {
      this.removeMeasurement(this.measurements[0]);
    }

    while (this.volumes.length > 0) {
      this.removeVolume(this.volumes[0]);
    }
  }

  setMeasurements(measurements: Measure[]) {
    this.removeAllMeasurements();

    for (const measurement of measurements) {
      this.addMeasurement(measurement);
    }
  }

  removeAllClipVolumes() {
    const clipVolumes = this.volumes.filter((volume) => volume.clip === true);
    for (const clipVolume of clipVolumes) {
      this.removeVolume(clipVolume);
    }

    while (this.polygonClipVolumes.length > 0) {
      this.removePolygonClipVolume(this.polygonClipVolumes[0]);
    }
  }

  getActiveCamera(): THREE.PerspectiveCamera | THREE.OrthographicCamera {
    if (this.overrideCamera) {
      return this.overrideCamera;
    }

    if (this.cameraMode === CameraMode.PERSPECTIVE) {
      return this.cameraP;
    } else if (this.cameraMode === CameraMode.ORTHOGRAPHIC) {
      return this.cameraO;
    } else if (this.cameraMode === CameraMode.VR) {
      return this.cameraVR;
    }

    return this.cameraP;
  }

  initialize() {
    this.referenceFrame = new THREE.Object3D();
    this.referenceFrame.matrixAutoUpdate = false;
    this.scenePointCloud.add(this.referenceFrame);

    this.cameraP.up.set(0, 0, 1);
    this.cameraP.position.set(1000, 1000, 1000);
    this.cameraO.up.set(0, 0, 1);
    this.cameraO.position.set(1000, 1000, 1000);
    //this.camera.rotation.y = -Math.PI / 4;
    //this.camera.rotation.x = -Math.PI / 6;
    // @ts-expect-error - lookAt called with Matrix4.lookAt signature (eye, target, up) instead of Object3D.lookAt
    this.cameraScreenSpace.lookAt(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, -1), new THREE.Vector3(0, 1, 0));

    this.directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
    this.directionalLight.position.set(10, 10, 10);
    this.directionalLight.lookAt(new THREE.Vector3(0, 0, 0));
    this.scenePointCloud.add(this.directionalLight);

    const light = new THREE.AmbientLight(0x555555); // soft white light
    this.scenePointCloud.add(light);

    {
      // background
      const texture = Utils.createBackgroundTexture(512, 512);

      texture.minFilter = texture.magFilter = THREE.NearestFilter;
      texture.minFilter = texture.magFilter = THREE.LinearFilter;
      const bg = new THREE.Mesh(
        new THREE.PlaneGeometry(2, 2, 1),
        new THREE.MeshBasicMaterial({
          map: texture,
        }),
      );
      bg.material.depthTest = false;
      bg.material.depthWrite = false;
      this.sceneBG.add(bg);
    }
  }
}
